// Every page = light hero panel (top bar + hero) over the lower section.
// Pages only describe their hero and lower content; the frame is shared.
import { MOCK_DATA } from '@/lib/api/mode';
import i18n, { useT } from '@/i18n';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router';
import { SUB_TABS, type PageMeta } from '@/app/nav';
import { MiniCard, type MiniCardProps } from '@/components/ui/Cards';
import { cx } from '@/lib/format';
import { TopBar } from './TopBar';
import { NavLock } from '@/features/plans/Upgrade';

export function PageFrame({ page, hero, lower, full = false }: { page: PageMeta; hero: ReactNode; lower?: ReactNode; full?: boolean }) {
  const root = useRef<HTMLDivElement>(null);
  const { t, i18n } = useT();
  useEffect(() => { document.title = `${page.title} — Orca`; }, [page.title, i18n.language]);
  const folded = useHeroFold(root, full);
  // stagger index for the entrance animation (same order as the concept)
  useLayoutEffect(() => {
    root.current?.querySelectorAll<HTMLElement>('.hero .tile, .hero .mini, .lower > *').forEach((el, i) => el.style.setProperty('--i', String(i)));
  });
  return (
    <div ref={root} style={{ display: 'contents' }}>
      <div className="panel">
        <div className="glow" aria-hidden="true"><i className="g1" /><i className="g2" /><i className="g3" /></div>
        <TopBar />
        <section className={cx('hero', 'enter', full && 'full')}>{hero}</section>
      </div>
      {!full && (
        <div className={cx('mini-panel', folded && 'show')} inert={!folded} aria-hidden={!folded}>
          <TopBar compact title={<button type="button" className="mini-title" title={t('action.toTop')} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>{page.title}<span aria-hidden="true">↑</span></button>} />
        </div>
      )}
      <main className="lower enter">{lower}</main>
      <footer className="ac-foot">
        <img className="logo-d" src="/assets/brand/orca-logo.png" alt="Orca" /><img className="logo-l" src="/assets/mascot/orca-logo-payment.png" alt="Orca" />
        <span>{t(MOCK_DATA ? 'footer.concept' : 'footer.beta')}</span>
        <span>{t('footer.slogan')}</span>
      </footer>
    </div>
  );
}

/**
 * Scroll-linked fold of the hero panel: while scrolling down the hero content
 * folds away (`--fold` 0→1 on the panel), and once the panel is out of view a
 * compact panel with the top bar slides in. Back at the top it unfolds.
 * Driven by scroll position (no layout changes), so the page never jumps.
 */
function useHeroFold(root: RefObject<HTMLDivElement | null>, off: boolean) {
  const [folded, setFolded] = useState(false);
  useEffect(() => {
    if (off) return;
    const panel = root.current?.querySelector<HTMLElement>('.panel');
    if (!panel) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const h = panel.offsetHeight;
      const y = window.scrollY;
      panel.style.setProperty('--fold', Math.min(1, Math.max(0, y / (h * 0.7))).toFixed(3));
      setFolded(y > panel.offsetTop + h - 90);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, [root, off]);
  return folded;
}

/** Section sub-tabs (links) or page-local tabs (buttons). */
export function SubTabs({ page, local }: { page: PageMeta; local?: { items: Array<[string, string]>; value: string; onChange: (v: string) => void } }) {
  if (local) {
    // a page-local view switch (DEV mock: crypto / stocks)
    return (
      <div className="subtabs" role="group">
        {local.items.map(([k, l]) => <button key={k} type="button" aria-pressed={k === local.value} className={cx(k === local.value && 'on')} onClick={() => local.onChange(k)}>{l}</button>)}
      </div>
    );
  }
  const items = SUB_TABS[page.group];
  if (!items) return null;
  // section navigation: real links, the current page marked for assistive tech
  return (
    <nav className="subtabs" aria-label={i18n.t('nav:sections')}>
      {items.map((p) => <Link key={p.path} to={p.path} className={cx(p.path === page.path && 'on')} aria-current={p.path === page.path ? 'page' : undefined}>{p.title}<NavLock path={p.path} /></Link>)}
    </nav>
  );
}

export type HeroProps = {
  page: PageMeta;
  titleSide?: ReactNode;
  tabs?: ReactNode;
  intro?: ReactNode;
  extra?: ReactNode;
  delta: ReactNode;
  big: ReactNode;
  cents?: ReactNode;
  note: ReactNode;
  long?: boolean;
  visual?: ReactNode;
  cards: MiniCardProps[];
  right: ReactNode;
};

/** Hero: title + tabs, one big metric with a visual, two mini cards; right column content. */
export function Hero({ page, titleSide, tabs, intro, extra, delta, big, cents, note, long, visual, cards, right }: HeroProps) {
  return (
    <>
      <div className="h-left">
        <div className="h-title-row"><h1 className="h-title">{page.title}</h1>{titleSide}</div>
        {tabs ?? <SubTabs page={page} />}
        {intro && <p className="h-intro">{intro}</p>}
        {extra}
        <div className="h-metric">
          <div className="h-num">
            <span className="h-delta">{delta}</span>
            <div className={cx('h-big', long && 'long')}><b>{big}</b>{cents && <small>{cents}</small>}<span className="h-note">{note}</span></div>
          </div>
          {visual && <div className="h-vis">{visual}</div>}
        </div>
        <div className="h-minis">{cards.map((c, i) => <MiniCard key={i} {...c} />)}</div>
      </div>
      <div className="h-right">{right}</div>
    </>
  );
}

export const spanStyle = (span: number) => ({ '--span': span }) as CSSProperties;
